// Slash commands available in the AI chat

export type SlashCommandCategory =
  | 'Core Workflow'
  | 'Planning & Collaboration'
  | 'Code Operations'
  | 'Session Management'
  | 'Autonomy Controls'
  | 'Skills & Extensions'
  | 'Documents & PDFs'
  | 'Creative & Design'
  | 'Media & Entertainment'
  | 'Web & Apps'
  | 'Analysis & Strategy'
  | 'Security & Ops'
  | 'Sales & Pipeline'
  | 'Communication'
  | 'Utilities & Help';

export interface SlashCommand {
  command: string;
  description: string;
  usage: string;
  action: 'builtin' | 'ai';
  category: SlashCommandCategory;
  aliases?: string[];
}

export const SLASH_COMMANDS: SlashCommand[] = [
  // Core workflow: goal -> plan -> code -> check
  {
    command: '/goal',
    aliases: ['/objective'],
    description: 'Set or update the current session goal for all AI agents',
    usage: '/goal Build auth for Tasking.tech with GitHub SSO',
    action: 'builtin',
    category: 'Core Workflow',
  },
  {
    command: '/plan',
    description:
      'Create an interactive plan — AI asks you guided multiple-choice questions before building',
    usage: '/plan <goal>',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/planfirst',
    aliases: ['/plan-first', '/think'],
    description:
      'Plan first, then execute — AI creates a step-by-step plan with reasoning and waits for your approval before acting',
    usage: '/planfirst <goal>',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/join-meeting',
    aliases: ['/meeting', '/join'],
    description:
      'Send TaskTaker to join a meeting — captures transcript, audio, screen recording, and generates notes',
    usage: '/join-meeting <meeting-url>',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/work',
    aliases: ['/build', '/go', '/start'],
    description: 'Run the goal -> plan -> code -> check loop for the requested steps',
    usage: '/work 2',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/status',
    description: 'Show current goal, task progress, checkpoints, branch, and agents',
    usage: '/status',
    action: 'builtin',
    category: 'Core Workflow',
  },
  {
    command: '/stop',
    aliases: ['/halt'],
    description: 'Stop the active goal -> plan -> code -> check loop immediately',
    usage: '/stop',
    action: 'builtin',
    category: 'Core Workflow',
  },
  // Planning & collaboration
  {
    command: '/discuss',
    description: 'Start a focused discussion thread with the AI team',
    usage: '/discuss Auth edge cases',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/brainstorm',
    description: 'Research an idea, build a structured plan, and create tasks from it',
    usage: '/brainstorm AI-powered recipe sharing app',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/huddle',
    description: 'Run a quick stand-up style sync with status updates',
    usage: '/huddle',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/focus',
    description: 'Point the AI team at a single task or file that needs attention',
    usage: '/focus Fix GitHub callback handler',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/feature',
    description: 'Co-design a feature or user story with the AI team',
    usage: '/feature SSO onboarding checklist',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/pr',
    description: 'Ask the AI to prepare or update a pull request description',
    usage: '/pr Add GitHub SSO',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/assign',
    description: 'Assign a task to a specific AI participant',
    usage: '/assign @Builder Implement rate limiter',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/history',
    description: 'Summarize recent decisions, actions, and checkpoints',
    usage: '/history',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/summary',
    description: 'Summarize the session with optional focus like tests or backend',
    usage: '/summary tests',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  // Code operations
  {
    command: '/review',
    description: 'Ask AI to review the current code or file for issues',
    usage: '/review pages/auth.tsx',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/debug',
    description: 'Collaboratively debug a failing flow or error',
    usage: '/debug GitHub redirect 500 error',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/refactor',
    description: 'Refactor a code path with help from the AI team',
    usage: '/refactor helpers/auth.ts',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/test',
    description: 'Generate, run, or discuss tests for the selected scope',
    usage: '/test auth callbacks',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/docs',
    description: 'Generate documentation or README updates for a feature',
    usage: '/docs GitHub SSO setup',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/explain',
    description: 'Have the AI explain the current file, diff, or concept',
    usage: '/explain components/AuthCallback.tsx',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/fix',
    description: 'Ask AI to fix errors or issues in the selected code',
    usage: '/fix oauth nonce mismatch',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/optimize',
    aliases: ['/opt'],
    description: 'Profile or optimize code and queries for better performance',
    usage: '/optimize helpers/tokenCache.ts',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/document',
    description: 'Add inline documentation or comments to the code',
    usage: '/document server/auth.ts',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/scaffold',
    description: 'Generate boilerplate for a component, API route, or utility',
    usage: '/scaffold component GithubConnectBanner',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/security',
    description: 'Run a quick security pass on code, configs, or dependencies',
    usage: '/security auth callbacks',
    action: 'ai',
    category: 'Code Operations',
  },
  // Session management
  {
    command: '/context',
    description: 'Show the current repo, branch, and active files',
    usage: '/context',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/branch',
    description: 'Switch to or create a Git branch for this work session',
    usage: '/branch feature/github-sso',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/files',
    description: 'List files in the current session or plan',
    usage: '/files',
    action: 'ai',
    category: 'Session Management',
  },
  {
    command: '/checkpoint',
    description: 'Create a named checkpoint of the workspace state',
    usage: '/checkpoint pre-sso-refactor',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/agents',
    description: 'Show active AI participants in the session',
    usage: '/agents',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/who',
    description: 'Show participants (people and AI agents) and their roles',
    usage: '/who',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/todo',
    description: 'Add a todo item for the AI team to track',
    usage: '/todo Update env docs for GitHub SSO',
    action: 'ai',
    category: 'Session Management',
  },
  {
    command: '/pickup',
    description: 'Assign yourself or an AI agent to an existing todo item',
    usage: '/pickup @Builder Update env docs',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/drop',
    description: 'Release a claimed todo so another participant can take it',
    usage: '/drop Update env docs',
    action: 'builtin',
    category: 'Session Management',
  },
  // Autonomy controls
  {
    command: '/auto',
    description: 'Toggle autonomous multi-agent mode (Conductor on or off)',
    usage: '/auto on',
    action: 'builtin',
    category: 'Autonomy Controls',
  },
  {
    command: '/limit',
    description: 'Set the maximum number of autonomous steps or messages',
    usage: '/limit 4',
    action: 'builtin',
    category: 'Autonomy Controls',
  },
  {
    command: '/rollback',
    description: 'Restore the most recent checkpoint by name',
    usage: '/rollback pre-sso-refactor',
    action: 'builtin',
    category: 'Autonomy Controls',
  },
  {
    command: '/submit',
    description: 'Ask the AI team to package current work for handoff or review',
    usage: '/submit',
    action: 'ai',
    category: 'Autonomy Controls',
  },
  {
    command: '/retract',
    description: 'Cancel the pending submission or undo the last autonomous step',
    usage: '/retract',
    action: 'builtin',
    category: 'Autonomy Controls',
  },
  // Utilities and help
  {
    command: '/cmdlist',
    description: 'Show all available slash commands grouped by category',
    usage: '/cmdlist',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/help',
    description: 'Get help and productivity tips for the platform',
    usage: '/help planning',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/clear',
    description: 'Clear the chat display locally while keeping history intact',
    usage: '/clear',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/clearattachment',
    description: 'Forget the last-used attachment/logo without clearing chat',
    usage: '/clearattachment',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/clearlogo',
    description: 'Alias for /clearattachment (clear saved logo/attachment)',
    usage: '/clearlogo',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/reset',
    description: 'Reset the AI conversation context completely',
    usage: '/reset',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/mute',
    description: 'Mute an AI participant for the session',
    usage: '/mute @Researcher',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/unmute',
    description: 'Unmute a previously muted AI participant',
    usage: '/unmute @Researcher',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/fire',
    description: 'Remove one or more AI participants with style (you are FIRED!)',
    usage: '/fire @Builder @Researcher',
    action: 'builtin',
    category: 'Session Management',
  },
  {
    command: '/mode',
    description: 'Switch between chat and code workspace modes',
    usage: '/mode code',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/model',
    description: 'Show or change the current AI model in use',
    usage: '/model GPT-5-Codex',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  {
    command: '/tip',
    description: 'Show a random productivity tip',
    usage: '/tip',
    action: 'builtin',
    category: 'Utilities & Help',
  },
  // Skills & Extensions
  {
    command: '/find',
    aliases: ['/search-skill'],
    description: 'Open the skill search to find and install agent skills',
    usage: '/find react performance',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/skills',
    description: 'Open the skills panel — browse, search, and install agent skills',
    usage: '/skills',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/add',
    aliases: ['/install-skill', '/skill-add'],
    description: 'Install an agent skill — adds a skill from a GitHub repo',
    usage: '/add https://github.com/anthropics/skills --skill frontend-design',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/install',
    aliases: ['/npm-install', '/pkg'],
    description: 'Install npm packages — adds packages to the project',
    usage: '/install lucide-react framer-motion clsx',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/skill-list',
    aliases: ['/my-skills'],
    description: 'List all currently installed agent skills',
    usage: '/skill-list',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/use-skill',
    aliases: ['/toggle-skill'],
    description: 'Show active skills, toggle a skill on/off, or search for new skills',
    usage: '/use-skill [name] [on|off]',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/skill-remove',
    aliases: ['/uninstall-skill'],
    description: 'Remove an installed agent skill',
    usage: '/skill-remove frontend-design',
    action: 'ai',
    category: 'Skills & Extensions',
  },
  {
    command: '/skilltree',
    aliases: ['/tree', '/packs', '/skill-packs', '/skillpack'],
    description: 'Open the skill tree — visualize and manage skill packs and bot assignments',
    usage: '/skilltree',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/create-pack',
    aliases: ['/new-pack'],
    description: 'Create a new skill pack with selected skills',
    usage: '/create-pack Frontend Design Pack',
    action: 'builtin',
    category: 'Skills & Extensions',
  },
  {
    command: '/audit',
    aliases: ['/health'],
    description:
      'Run a full project audit — code quality, pending issues, progress, and recommendations',
    usage: '/audit',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/report',
    aliases: ['/status-report'],
    description:
      "Summarize current system state — what's working, what's broken, what needs fixing with P0–P3 priority tags",
    usage: '/report',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/timeline',
    aliases: ['/activity-log', '/changelog'],
    description:
      'Show chronological view of recent activity — deploys, config changes, errors/incidents, feature additions, and upcoming work',
    usage: '/timeline',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/investigate',
    aliases: ['/deep-dive', '/root-cause'],
    description:
      'Deep-dive into a specific issue — gather logs/errors, form root cause hypothesis, outline diagnostic steps and proposed fix',
    usage: '/investigate auth redirect 500 error',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/bug',
    aliases: ['/bug-report', '/defect'],
    description:
      'Log a confirmed defect — steps to reproduce, expected vs actual, severity P0–P3, owner, and linked /investigate or /timeline entries',
    usage: '/bug Login button unresponsive on mobile Safari',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/problem',
    aliases: ['/problem-report', '/issue'],
    description:
      'Log a broader issue that may not be a clear bug — symptoms, user impact, affected feature/flow, and hypotheses for UX, perf, or flaky behavior',
    usage: '/problem Preview loads slowly on large projects',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/breakdown',
    aliases: ['/decompose', '/split'],
    description:
      'Deconstruct a feature, issue, or idea into sub-tasks, dependencies, risks, unknowns, and a checklist for tickets',
    usage: '/breakdown user authentication flow',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/scoop',
    aliases: ['/intel', '/briefing'],
    description:
      "Quick intel dump — latest status, key facts, who's involved, recent changes, open questions, and relevant logs/links",
    usage: '/scoop deployment pipeline',
    action: 'ai',
    category: 'Planning & Collaboration',
  },
  {
    command: '/redesign',
    aliases: ['/restyle', '/design'],
    description: 'Apply frontend-design skill principles to restyle the current project',
    usage: '/redesign modern dark theme with glass morphism',
    action: 'ai',
    category: 'Code Operations',
  },
  {
    command: '/regenerate-images',
    aliases: ['/regen-images', '/gen-images'],
    description: 'Regenerate all images in the project using AI (Gemini) to match the site design',
    usage: '/regenerate-images hero banner with dark tech aesthetic',
    action: 'ai',
    category: 'Code Operations',
  },

  // Documents & PDFs
  {
    command: '/sow',
    description: 'Generate a professional Statement of Work PDF with cover page, deliverables, Gantt chart, pricing, and signatures',
    usage: '/sow Azure AVD vGPU migration for Intellisense Systems',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/proposal',
    description: 'Generate a business proposal PDF with executive summary, scope, timeline, and pricing',
    usage: '/proposal Enterprise SaaS platform for healthcare compliance',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/report',
    description: 'Generate a professional report PDF with data, charts, and findings',
    usage: '/report Q1 2026 marketing performance analysis',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/resume',
    aliases: ['/cv'],
    description: 'Generate a professional resume/CV PDF',
    usage: '/resume John Smith, 10yr senior software engineer, React/Node/AWS',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/coverletter',
    aliases: ['/cover-letter'],
    description: 'Generate a professional cover letter PDF',
    usage: '/coverletter Senior Engineer at Google, 8yr experience in distributed systems',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/newsletter',
    description: 'Generate a professional newsletter PDF with articles and images',
    usage: '/newsletter March 2026 AI industry roundup',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/flyer',
    description: 'Generate a promotional sales flyer PDF with images and call-to-action',
    usage: '/flyer Summer sale 40% off all cloud services',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/techdocs',
    aliases: ['/tech-docs'],
    description: 'Generate technical documentation PDF with API reference, architecture diagrams, and code examples',
    usage: '/techdocs TaskingBot REST API v2',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/manual',
    description: 'Generate a user manual PDF with setup guides, feature walkthroughs, and troubleshooting',
    usage: '/manual TaskingBot platform user guide',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/faq',
    description: 'Generate a FAQ document PDF organized by category',
    usage: '/faq Common questions about our SaaS pricing and features',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/book',
    aliases: ['/ebook'],
    description: 'Generate a book/ebook PDF with chapters, illustrations, and professional layout',
    usage: '/book "Mastering AI Agents" - a guide to building autonomous systems',
    action: 'ai',
    category: 'Documents & PDFs',
  },

  {
    command: '/convertdoc',
    aliases: ['/pdf2docx', '/pdftodocx', '/pdf-to-docx', '/convert-pdf'],
    description: 'Convert an uploaded PDF to an editable DOCX (Word) file — preserves images, colors, and layout',
    usage: '/convertdoc [attach a PDF file]',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/vectorize',
    aliases: ['/svg', '/tovector', '/imgtosvg', '/raster2vector'],
    description: 'Convert any raster image (PNG/JPG) to an ultra-high-res SVG — imports into Visio, draw.io, Excalidraw, Inkscape',
    usage: '/vectorize [attach an image or paste an image URL]',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/drawio',
    aliases: ['/diagram-editable', '/editable-diagram', '/mxgraph'],
    description: 'Generate an editable diagram as a draw.io / Visio file (rectangles, arrows, swimlanes — fully editable shapes)',
    usage: '/drawio microservices architecture for an e-commerce platform',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/powerpoint',
    aliases: ['/ppt', '/presentation', '/slides', '/slideshow'],
    description: 'Generate a PowerPoint-style presentation PDF with slides, speaker notes, and visuals',
    usage: '/powerpoint Q1 2026 company all-hands meeting',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/diagram',
    aliases: ['/flowchart'],
    description: 'Generate professional diagrams — architecture, flowcharts, ERDs, network topology, and more',
    usage: '/diagram microservices architecture for e-commerce platform',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/workflow',
    description: 'Generate a workflow diagram with steps, decision points, and process flow',
    usage: '/workflow customer onboarding process from signup to first project',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/swimlane',
    aliases: ['/swim-lane'],
    description: 'Generate a swimlane diagram showing process steps across roles/departments',
    usage: '/swimlane bug report lifecycle across QA, Engineering, and Product teams',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/gantt',
    aliases: ['/timeline', '/schedule'],
    description: 'Generate a Gantt chart project timeline PDF with phases, milestones, and dependencies',
    usage: '/gantt 6-month mobile app development project with 4 sprints',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/raci',
    description: 'Generate a RACI matrix PDF — Responsible, Accountable, Consulted, Informed',
    usage: '/raci Cloud migration project with DevOps, Security, Engineering, and PM teams',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/research',
    aliases: ['/deepresearch', '/study'],
    description: 'Generate a deep research report PDF with analysis, citations, data visualizations, and recommendations',
    usage: '/research Market analysis of AI agent platforms in 2026',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/document',
    aliases: ['/doc', '/pdf'],
    description: 'Generate a general-purpose professional document PDF — adapts format to your content',
    usage: '/document Employee handbook for remote-first startup',
    action: 'ai',
    category: 'Documents & PDFs',
  },
  {
    command: '/specs',
    aliases: ['/spec', '/prd', '/requirements'],
    description: 'Generate a technical specification or product requirements document PDF',
    usage: '/specs Real-time collaboration feature for TaskingBot',
    action: 'ai',
    category: 'Documents & PDFs',
  },

  // Creative & Design
  {
    command: '/logo',
    description: 'Generate logo design variations for a brand',
    usage: '/logo TaskingTech - modern tech company, orange and green colors',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/tshirtlogo',
    aliases: ['/tshirt'],
    description: 'Generate print-ready t-shirt and merchandise designs',
    usage: '/tshirtlogo "Code & Coffee" developer meetup merch',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/poster',
    description: 'Generate poster designs for events, products, or campaigns',
    usage: '/poster AI Developer Conference 2026 - June 15th San Francisco',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/sign',
    description: 'Generate signage designs for stores, offices, or events',
    usage: '/sign "Welcome to TaskingTech HQ" modern office entrance sign',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/sticker',
    description: 'Generate die-cut style sticker designs',
    usage: '/sticker cute robot mascot for developer sticker pack',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/emoji',
    description: 'Generate custom emoji and icon designs',
    usage: '/emoji set of 6 developer-themed emojis for Slack',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/posts',
    aliases: ['/post'],
    description: 'Generate social media posts with images for LinkedIn, Facebook, and X',
    usage: '/posts Announcing our new AI-powered task management feature',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/banner',
    description: 'Generate web/social media banner designs',
    usage: '/banner Holiday sale 50% off - 1200x628 Facebook ad',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/decal',
    description: 'Generate decal and vinyl cut designs',
    usage: '/decal Company logo for vehicle wrap',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/card',
    aliases: ['/bizcard', '/businesscard'],
    description: 'Generate professional business card designs with front and back layouts',
    usage: '/card John Smith, CEO, TaskingTech, john@tasking.tech, +1-555-0123',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/drhouse',
    aliases: ['/house', '/diagnose'],
    description: 'Diagnose a problem like Dr. House — sarcastic, brilliant differential diagnosis with dramatic reveals',
    usage: '/drhouse Our API response times tripled after the last deploy',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/meme',
    aliases: ['/memes'],
    description: 'Generate a custom meme image with top/bottom text and humorous visuals',
    usage: '/meme When the deployment works on the first try',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/qrcode',
    aliases: ['/qr'],
    description: 'Generate a styled QR code with custom colors, logo embed, and branding',
    usage: '/qrcode https://tasking.tech — orange brand colors with logo',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/3d',
    description: 'Generate 3D render concept art and visualizations',
    usage: '/3d Futuristic server room with glowing racks',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/3dasset',
    aliases: ['/3d-asset'],
    description: 'Generate 3D asset reference sheets with multiple angles and specs',
    usage: '/3dasset Low-poly robot character for mobile game',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/3dprint',
    aliases: ['/3d-print'],
    description: 'Generate 3D printable design concepts with dimensions and specs',
    usage: '/3dprint Custom phone stand with cable management',
    action: 'ai',
    category: 'Creative & Design',
  },
  {
    command: '/headshots',
    aliases: ['/headshot', '/portrait', '/profilepic', '/profile-pic'],
    description: 'Generate professional headshot/portrait photos in multiple styles',
    usage: '/headshots Professional woman with short brown hair, corporate setting',
    action: 'ai',
    category: 'Creative & Design',
  },

  // Media & Entertainment
  {
    command: '/musicvideo',
    aliases: ['/music-video'],
    description: 'Generate a music video concept with storyboard frames and video clips',
    usage: '/musicvideo Synthwave retro aesthetic for electronic track',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/tvshow',
    aliases: ['/tv-show', '/show'],
    description: 'Generate a TV show concept with pilot outline, character art, and visuals',
    usage: '/tvshow Sci-fi drama about AI agents becoming sentient',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/cartoon',
    aliases: ['/animation'],
    description: 'Generate cartoon/animation concept art, character designs, and storyboard',
    usage: '/cartoon Kids educational show about coding with robot characters',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/video-anime',
    aliases: ['/anime-video'],
    description: 'Generate an anime-style AI video clip in chat',
    usage: '/video-anime Epic mecha battle in neon-lit Tokyo',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/video-cartoon',
    aliases: ['/cartoon-video'],
    description: 'Generate a cartoon-style AI video clip in chat',
    usage: '/video-cartoon Friendly animals learning teamwork in a forest adventure',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/video-cinematic',
    aliases: ['/cinematic-video', '/film'],
    description: 'Generate a cinematic film-quality AI video clip in chat',
    usage: '/video-cinematic Dramatic ocean sunset with lighthouse silhouette',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/video-promo',
    aliases: ['/promo-video'],
    description: 'Generate a promotional AI video clip in chat',
    usage: '/video-promo SaaS product launch with sleek UI animations',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/video-commercial',
    aliases: ['/commercial-video', '/ad-video'],
    description: 'Generate a commercial/advertisement AI video clip in chat',
    usage: '/video-commercial Premium coffee brand morning routine ad',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/comic',
    aliases: ['/comicbook'],
    description: 'Generate comic strip or comic book pages with panels and dialogue',
    usage: '/comic 4-panel strip about a developer debugging at 3am',
    action: 'ai',
    category: 'Media & Entertainment',
  },
  {
    command: '/game',
    aliases: ['/gamedesign'],
    description: 'Generate a game design document with concept art, mechanics, and level design',
    usage: '/game Puzzle platformer with gravity manipulation mechanics',
    action: 'ai',
    category: 'Media & Entertainment',
  },

  // Web & Apps
  {
    command: '/website',
    aliases: ['/site'],
    description: 'Generate and deploy a complete website',
    usage: '/website Portfolio site for a photographer with dark theme',
    action: 'ai',
    category: 'Web & Apps',
  },
  {
    command: '/webapp',
    aliases: ['/app'],
    description: 'Generate and deploy a web application',
    usage: '/webapp Kanban board with drag-and-drop and user auth',
    action: 'ai',
    category: 'Web & Apps',
  },

  // Analysis & Strategy
  {
    command: '/analyze',
    aliases: ['/analysis'],
    description: 'Deep analysis with data visualizations, insights, and actionable recommendations',
    usage: '/analyze Our Q1 customer churn data shows 15% increase',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/debug',
    description: 'Systematic debugging analysis with root cause identification and fix recommendations',
    usage: '/debug Users getting 502 errors on the /api/tasks endpoint intermittently',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/rca',
    aliases: ['/rootcause'],
    description: 'Root Cause Analysis report with incident timeline, 5-Whys, corrective actions, and prevention plan',
    usage: '/rca Production database outage on March 15th that lasted 2 hours',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/troubleshoot',
    aliases: ['/tshoot'],
    description: 'Step-by-step troubleshooting guide with decision tree and resolution paths',
    usage: '/troubleshoot Kubernetes pods keep crashing with OOMKilled',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/proscons',
    aliases: ['/pros-cons', '/tradeoff'],
    description: 'Pros and cons comparison matrix with weighted scoring and recommendation',
    usage: '/proscons AWS vs GCP vs Azure for our ML pipeline',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/rating',
    aliases: ['/score', '/evaluate'],
    description: 'Detailed rating and scoring analysis with criteria breakdown and visual scorecards',
    usage: '/rating Top 5 project management tools for remote teams',
    action: 'ai',
    category: 'Analysis & Strategy',
  },
  {
    command: '/compare',
    aliases: ['/versus', '/vs'],
    description: 'Side-by-side comparison with charts, feature matrices, and recommendations',
    usage: '/compare React vs Vue vs Svelte for our new frontend',
    action: 'ai',
    category: 'Analysis & Strategy',
  },

  // Security & Ops
  {
    command: '/secreport',
    aliases: ['/security-report', '/securityreport'],
    description: 'Generate a security assessment report with findings, risk ratings, and remediation steps',
    usage: '/secreport Security audit findings for our Node.js API',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/threatanalysis',
    aliases: ['/threat', '/threat-analysis'],
    description: 'Generate a threat analysis report with attack vectors, risk matrix, and mitigation strategies',
    usage: '/threatanalysis Threat model for our payment processing system',
    action: 'ai',
    category: 'Security & Ops',
  },

  {
    command: '/pentest',
    aliases: ['/pen-test', '/penetrationtest', '/scan', '/metatron', '/security-scan'],
    description: 'Run METATRON penetration test — real security scans (headers, SSL, DNS, ports, vulnerabilities, subdomains)',
    usage: '/pentest https://example.com',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/vulnerabilityreport',
    aliases: ['/vulnreport', '/vuln-report', '/cve'],
    description: 'Generate a vulnerability assessment report with severity ratings and patch recommendations',
    usage: '/vulnerabilityreport Scan results for our Node.js production servers',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/threatmatrix',
    aliases: ['/threat-matrix'],
    description: 'Generate a threat matrix mapping attack vectors, threat actors, and risk scores',
    usage: '/threatmatrix Cloud infrastructure threat model for AWS multi-account setup',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/secaudit',
    aliases: ['/sec-audit', '/securityaudit'],
    description: 'Generate a security audit report with policy review, access control checks, and findings',
    usage: '/secaudit Annual security audit for our SaaS platform',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/compliancereport',
    aliases: ['/compliance', '/soc2', '/hipaa', '/gdpr', '/cmmcv1', '/cmmcv2', '/cmmc', '/fips', '/pcidss', '/pci', '/iso27001', '/nist', '/pii', '/phi'],
    description: 'Generate a compliance assessment report against frameworks like SOC2, HIPAA, GDPR, PCI-DSS, CMMC, FIPS, NIST, ISO 27001, PII/PHI',
    usage: '/compliancereport SOC2 Type II readiness assessment for TaskingTech',
    action: 'ai',
    category: 'Security & Ops',
  },

  {
    command: '/mxtest',
    aliases: ['/mx', '/mxtoolbox', '/mailtest', '/dnsmail'],
    description: 'Test mail records for a domain — MX, SPF, DKIM, DMARC, PTR, blacklist checks, and deliverability analysis',
    usage: '/mxtest tasking.tech',
    action: 'ai',
    category: 'Security & Ops',
  },

  {
    command: '/dnslookup',
    aliases: ['/dns', '/dnscheck', '/nslookup', '/dig'],
    description: 'Comprehensive DNS lookup — A, AAAA, CNAME, MX, NS, TXT, SOA, CAA records',
    usage: '/dnslookup tasking.tech',
    action: 'ai',
    category: 'Security & Ops',
  },
  {
    command: '/traceroute',
    aliases: ['/tracert', '/networkpath', '/ping'],
    description: 'Network path analysis — hosting provider, CDN detection, latency estimation, topology',
    usage: '/traceroute api.tasking.tech',
    action: 'ai',
    category: 'Security & Ops',
  },

  // Sales & Pipeline
  {
    command: '/leads',
    aliases: ['/pipeline', '/prospects', '/crm'],
    description: 'Manage your sales pipeline — view leads, add prospects, run AI agents to find and qualify leads',
    usage: '/leads',
    action: 'ai',
    category: 'Sales & Pipeline',
  },
  {
    command: '/prospect',
    aliases: ['/find-leads', '/hunt'],
    description: 'Deploy AI Lead Hunter — searches for prospects matching your ICP and adds them to your pipeline',
    usage: '/prospect B2B SaaS companies in fintech, 50-200 employees, Series A-B',
    action: 'ai',
    category: 'Sales & Pipeline',
  },
  {
    command: '/icp',
    aliases: ['/ideal-customer', '/target'],
    description: 'Define or update your Ideal Customer Profile — industry, company size, titles, tech stack',
    usage: '/icp SaaS startups, 20-500 employees, using React, VP/Director engineering',
    action: 'ai',
    category: 'Sales & Pipeline',
  },
  {
    command: '/outreach',
    aliases: ['/sequence', '/cadence'],
    description: 'Create personalized outreach sequences — email, LinkedIn, multi-channel campaigns',
    usage: '/outreach Cold email sequence for DevTool CTOs',
    action: 'ai',
    category: 'Sales & Pipeline',
  },
  {
    command: '/enrich',
    aliases: ['/research-lead', '/account-research'],
    description: 'Enrich lead profiles — company intel, tech stack, funding, decision-makers',
    usage: '/enrich TechCorp',
    action: 'ai',
    category: 'Sales & Pipeline',
  },
  {
    command: '/score',
    aliases: ['/qualify', '/rank-leads'],
    description: 'Score and qualify leads against your ICP — rank by fit, engagement, and intent signals',
    usage: '/score',
    action: 'ai',
    category: 'Sales & Pipeline',
  },

  // Communication
  {
    command: '/tip',
    aliases: ['/protip'],
    description: 'Generate a professional tip or best-practice guide with visuals',
    usage: '/tip 5 tips for securing your Kubernetes cluster',
    action: 'ai',
    category: 'Communication',
  },
  {
    command: '/news',
    aliases: ['/newsbrief'],
    description: 'Generate a news brief or industry update with analysis and key takeaways',
    usage: '/news Latest developments in AI agent frameworks March 2026',
    action: 'ai',
    category: 'Communication',
  },
  {
    command: '/email',
    aliases: ['/mail'],
    description: 'Draft a professional email with proper formatting, tone, and structure',
    usage: '/email Follow-up to client about project timeline delay',
    action: 'ai',
    category: 'Communication',
  },

  // Utilities & Help (additional)
  {
    command: '/log',
    aliases: ['/logs', '/history', '/activity'],
    description: 'View activity logs, task history, or conversation history',
    usage: '/log last 7 days of task activity',
    action: 'ai',
    category: 'Utilities & Help',
  },
  {
    command: '/login',
    aliases: ['/auth', '/authenticate'],
    description: 'Login or authenticate to an external service via TaskingBot',
    usage: '/login GitHub',
    action: 'ai',
    category: 'Utilities & Help',
  },
  {
    command: '/select',
    aliases: ['/switch', '/pick'],
    description: 'Select or switch between tasks, projects, conversations, or workspaces',
    usage: '/select project TaskingBot v2',
    action: 'ai',
    category: 'Utilities & Help',
  },
  {
    command: '/find',
    aliases: ['/search', '/lookup'],
    description: 'Search across tasks, files, messages, and external sources',
    usage: '/find all open bugs assigned to me',
    action: 'ai',
    category: 'Utilities & Help',
  },
  {
    command: '/chat',
    aliases: ['/talk', '/converse'],
    description: 'Switch to conversational mode — talk to TaskingBot without generating documents or taking actions',
    usage: '/chat',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/tasks',
    aliases: ['/mytasks', '/tasklist'],
    description: 'Show current tasks and their status',
    usage: '/tasks',
    action: 'ai',
    category: 'Core Workflow',
  },
  {
    command: '/sendsms',
    aliases: ['/sms', '/text'],
    description: 'Send a text message to a phone number',
    usage: '/sendsms +1234567890 Hey, the deployment is done!',
    action: 'ai',
    category: 'Communication',
  },
  {
    command: '/mockup',
    aliases: ['/mock', '/wireframe', '/prototype'],
    description: 'Generate a UI mockup, wireframe, or prototype visualization',
    usage: '/mockup Dashboard layout for user analytics',
    action: 'ai',
    category: 'Creative & Design',
  },
];

export const CATEGORY_ORDER: SlashCommandCategory[] = [
  'Core Workflow',
  'Planning & Collaboration',
  'Code Operations',
  'Documents & PDFs',
  'Creative & Design',
  'Media & Entertainment',
  'Web & Apps',
  'Analysis & Strategy',
  'Sales & Pipeline',
  'Security & Ops',
  'Communication',
  'Session Management',
  'Autonomy Controls',
  'Skills & Extensions',
  'Utilities & Help',
];

// Tips shown periodically to help users
export const TIPS: string[] = [
  '💡 Tip: Use /goal, /plan, and /work to run the full AI build loop',
  '💡 Tip: Press Ctrl+Enter to send a message quickly',
  '💡 Tip: /status shows the goal, branch, and active tasks at any time',
  '💡 Tip: Use @mentions to directly address AI participants in the chat',
  '💡 Tip: Drag and drop images directly into the chat to share them',
  '💡 Tip: Toggle Auto-Execute with the ⚡ button to let AI run code automatically',
  '💡 Tip: Use /checkpoint before risky changes so you can /rollback later',
  '💡 Tip: Click on file paths in "Files changed" to open them in the editor',
  '💡 Tip: Use /review to get AI feedback on your code',
  '💡 Tip: Add AI participants to get multiple perspectives on your code',
  '💡 Tip: Use voice input by clicking the 🎤 microphone button',
  '💡 Tip: Switch between Chat and Code mode using the toggle button',
  '💡 Tip: Enable Web Search to let AI fetch up-to-date information',
  '💡 Tip: Use /fix followed by an error message to get quick fixes',
  '💡 Tip: Press ↑/↓ arrows to navigate through your message history',
  '💡 Tip: Use /explain when a file or diff needs clarification',
  '💡 Tip: The Conductor feature can orchestrate multiple AI agents on complex tasks',
  '💡 Tip: Use annotations to draw on screenshots and share visual feedback',
  '💡 Tip: Use /pickup to claim a todo item for yourself or an agent',
  '💡 Tip: AI participants can work together autonomously when given a goal',
];

// Get a random tip
export function getRandomTip(): string {
  return TIPS[Math.floor(Math.random() * TIPS.length)];
}

// Format command list as markdown for display
export function formatCommandList(): string {
  let output = '## Available Slash Commands\n\n';

  for (const category of CATEGORY_ORDER) {
    const commands = SLASH_COMMANDS.filter((c) => c.category === category);
    if (commands.length === 0) continue;

    output += `### ${category}\n\n`;
    output += '| Command | Aliases | Description | Usage | Action |\n';
    output += '|---------|---------|-------------|-------|--------|\n';
    for (const cmd of commands) {
      const aliases =
        cmd.aliases && cmd.aliases.length > 0 ? cmd.aliases.map((a) => `\`${a}\``).join(', ') : '-';
      const actionLabel = cmd.action === 'ai' ? 'AI' : 'Built-in';
      output += `| \`${cmd.command}\` | ${aliases} | ${cmd.description} | \`${cmd.usage}\` | ${actionLabel} |\n`;
    }
    output += '\n';
  }

  output += '\n---\n';
  output += getRandomTip();

  return output;
}

// Parse a message to check if it's a slash command (anywhere in the message)
export function parseSlashCommand(
  message: string,
): { command: string; args: string; prefix: string } | null {
  const trimmed = message.trim();

  // Try start-of-message first (original behavior)
  if (trimmed.startsWith('/')) {
    // Skip URLs, file paths, API endpoints — only treat single-word /commands as slash commands
    if (trimmed.startsWith('/_') || trimmed.startsWith('/api') || trimmed.startsWith('/http') ||
        trimmed.includes('://') || trimmed.includes('.com') || trimmed.includes('.tech') ||
        /^\/[\w-]+\//.test(trimmed)) {
      // Looks like a URL or path, not a command — skip
    } else {
      const spaceIndex = trimmed.indexOf(' ');
      if (spaceIndex === -1) {
        return { command: trimmed.toLowerCase(), args: '', prefix: '' };
      }
      return {
        command: trimmed.substring(0, spaceIndex).toLowerCase(),
        args: trimmed.substring(spaceIndex + 1).trim(),
        prefix: '',
      };
    }
  }

  // Search for slash command anywhere in the message (after whitespace or newline)
  const match = trimmed.match(/(?:^|\s)(\/[a-z][\w-]*)(?:\s(.*))?$/i);
  if (match) {
    const command = match[1].toLowerCase();
    const args = (match[2] || '').trim();
    const cmdStart = trimmed.indexOf(match[1]);
    const prefix = trimmed.substring(0, cmdStart).trim();

    // Only treat as a command if it's a valid registered command
    if (isValidCommand(command)) {
      return { command, args, prefix };
    }
  }

  return null;
}

// Check if a command exists (checking primary command and aliases)
export function isValidCommand(command: string): boolean {
  const lowerCmd = command.toLowerCase();
  return SLASH_COMMANDS.some(
    (c) => c.command === lowerCmd || (c.aliases && c.aliases.includes(lowerCmd)),
  );
}

// Get command info (checking primary command and aliases)
export function getCommandInfo(command: string): SlashCommand | undefined {
  const lowerCmd = command.toLowerCase();
  return SLASH_COMMANDS.find(
    (c) => c.command === lowerCmd || (c.aliases && c.aliases.includes(lowerCmd)),
  );
}
